import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../../services/useAuth";
import { useTitle } from "../../../hooks/useTitle";
import { translate } from "../../../hooks/translate";
import { useModalConfirm as confirm } from "../../../hooks/useModalConfirm";
import { withAuth } from "../../../components/HOC/withAuth";
import { hasPageWriteAccess } from "../../../config/roles";
import {
  formatDateTimeTrInAppTz,
  formatInstantDateShortTr,
} from "../../../utils/appTime";
import AppPage from "../../../components/CustomUI/AppPage";
import CardContainer from "../../../components/CustomUI/CardContainer";
import Button from "../../../components/BaseUI/Button";
import Lozenge from "../../../components/BaseUI/Lozenge";
import { ToastContainer, Toast } from "../../../components/CustomUI/Toast";
import {
  FiPackage,
  FiEdit2,
  FiArrowLeft,
  FiTrash2,
  FiTruck,
  FiUser,
  FiCalendar,
  FiFileText,
  FiShoppingCart,
  FiHome,
  FiUsers,
  FiRefreshCw,
  FiChevronDown,
  FiChevronUp,
} from "react-icons/fi";
import { generateDeliveryProductsPDF } from "../../../utils/generateOfferPDF";
import PdfPreviewModal from "../../../components/CustomUI/PdfPreviewModal";
import { usePdfPreview } from "../../../hooks/usePdfPreview";
import "./StockTransfers.scss";

const API_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:3100";

const ViewDelivery = () => {
  const { deliveryId: id } = useParams();
  const { getToken, getUser } = useAuth();
  const navigate = useNavigate();
  const currentUser = getUser();
  const canWrite =
    !!currentUser &&
    hasPageWriteAccess(
      currentUser.role,
      "/deliveries",
      currentUser.pages || [],
      !!currentUser.fullAccess,
    );
  const [transfer, setTransfer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toasts, setToasts] = useState([]);
  const [expandedModels, setExpandedModels] = useState({});
  const [modelDevicePages, setModelDevicePages] = useState({});
  const modelDevicesPerPage = 10;
  const {
    pdfPreview,
    closePdfPreview,
    openPdfPreview,
    downloadPdfFromPreview,
  } = usePdfPreview();

  useTitle("Rovion MES - Sevkiyat Detayları");

  const addToast = (message, type = "info") => {
    const newToast = { id: Date.now(), message, type };
    setToasts([...toasts, newToast]);
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== newToast.id));
    }, 3000);
  };

  useEffect(() => {
    fetchTransfer();
  }, [id]);

  const fetchTransfer = async () => {
    try {
      setLoading(true);
      const token = getToken();
      const response = await axios.get(`${API_URL}/api/deliveries/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTransfer(response.data?.delivery || response.data);
    } catch (error) {
      console.error("Failed to fetch delivery:", error);
      addToast("Sevkiyat yüklenemedi", "danger");
    } finally {
      setLoading(false);
    }
  };

  const getModelKey = (modelGroup) => {
    return (
      modelGroup?.modelId ||
      `${modelGroup?.modelName || "model"}-${modelGroup?.modelCode || ""}`
    );
  };

  const toggleModelExpanded = (modelGroup) => {
    const key = String(getModelKey(modelGroup));
    setExpandedModels((prev) => {
      const next = { ...(prev || {}) };
      const newValue = !next[key];
      next[key] = newValue;
      return next;
    });
    setModelDevicePages((prev) => {
      const next = { ...(prev || {}) };
      // reset page whenever toggling (especially when opening)
      next[key] = 1;
      return next;
    });
  };

  const setModelPage = (modelGroup, page) => {
    const key = String(getModelKey(modelGroup));
    setModelDevicePages((prev) => ({ ...(prev || {}), [key]: page }));
  };

  const handleDelete = async () => {
    confirm({
      title: translate("UI.DELETE_DELIVERY") || "Sevkiyatı Sil",
      message:
        translate("UI.DELETE_DELIVERY_CONFIRM") ||
        "Bu sevkiyatı silmek istediğinizden emin misiniz?",
      appearance: "danger",
      confirmButtonText: translate("UI.DELETE") || "Sil",
      cancelButtonText: translate("UI.CANCEL") || "İptal",
      onConfirm: async () => {
        try {
          const token = getToken();
          await axios.delete(`${API_URL}/api/deliveries/${id}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          addToast("Sevkiyat başarıyla silindi", "success");
          setTimeout(() => navigate("/deliveries"), 1000);
        } catch (error) {
          console.error("Failed to delete delivery:", error);
          addToast(
            error.response?.data?.message || "Sevkiyat silinemedi",
            "danger",
          );
        }
      },
    });
  };

  const handlePreviewDeliveryPdf = () => {
    if (!transfer) return;
    const deliveryName =
      transfer.deliveryName ||
      transfer.saleId?.saleName ||
      `Sevkiyat #${String(transfer._id).slice(-6)}`;
    openPdfPreview({
      title: `Sevkiyat PDF — ${deliveryName}`,
      generate: (currencyOptions) =>
        generateDeliveryProductsPDF(transfer, {
          action: "blob",
          ...currencyOptions,
        }),
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "-";
    return formatDateTimeTrInAppTz(dateString);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return formatInstantDateShortTr(dateString);
  };

  if (loading) {
    return (
      <AppPage title="Sevkiyat Detayları" icon={<FiPackage />}>
        <CardContainer>
          <div className="loading-container">
            <FiRefreshCw className="spin" size={48} />
            <p>Yükleniyor...</p>
          </div>
        </CardContainer>
      </AppPage>
    );
  }

  if (!transfer) {
    return (
      <AppPage title="Sevkiyat Detayları" icon={<FiPackage />}>
        <CardContainer>
          <div className="empty-state">
            <FiPackage size={64} />
            <p>Sevkiyat bulunamadı</p>
            <Button
              appearance="primary"
              onClick={() => navigate("/deliveries")}
              icon={<FiArrowLeft />}
            >
              Geri Dön
            </Button>
          </div>
        </CardContainer>
      </AppPage>
    );
  }

  // Group devices by model
  const modelGroups = {};
  const totalDevices = (transfer.devices || []).length;
  (transfer.devices || []).forEach((device) => {
    const modelName =
      device?.modelId?.name || device?.model?.name || "Bilinmeyen Model";
    const modelId =
      device?.modelId?._id || device?.modelId || device?.model?._id;
    const modelCode = device?.modelId?.code || device?.model?.code || "";

    if (!modelGroups[modelName]) {
      modelGroups[modelName] = {
        modelId: modelId,
        modelName: modelName,
        modelCode: modelCode,
        devices: [],
      };
    }
    modelGroups[modelName].devices.push(device);
  });

  return (
    <AppPage
      title={transfer.saleId?.saleName || "Sevkiyat Detayları"}
      subtitle={`Sevkiyat #${transfer._id?.slice(-6)}`}
      icon={<FiPackage />}
      actions={
        <div className="d-flex gap-2">
          <Button
            appearance="primary-subtle"
            onClick={handlePreviewDeliveryPdf}
            icon={<FiFileText />}
          >
            PDF Önizle
          </Button>
          <Button
            appearance="danger-subtle"
            onClick={() => navigate("/deliveries")}
            icon={<FiArrowLeft />}
          >
            Geri
          </Button>
          {canWrite && (
            <>
              <Button
                appearance="primary"
                onClick={() => navigate(`/deliveries/edit/${id}`)}
                icon={<FiEdit2 />}
              >
                Düzenle
              </Button>
              <Button
                appearance="danger"
                onClick={handleDelete}
                icon={<FiTrash2 />}
              >
                Sil
              </Button>
            </>
          )}
        </div>
      }
    >
      <CardContainer className="p-4">
        {/* Header Info Card */}
        <div className="delivery-info-card">
          <div className="delivery-info-card__header">
            <div className="delivery-info-card__avatar">
              <FiPackage size={24} />
            </div>
            <div className="delivery-info-card__content">
              <h3>
                {transfer.saleId?.saleName || "Sevkiyat"} #
                {transfer._id?.slice(-6)}
              </h3>
              <div className="delivery-info-card__meta">
                {transfer.deliveryDate && (
                  <span className="meta-item">
                    <FiCalendar size={14} />
                    {formatDate(transfer.deliveryDate)}{" "}
                    {transfer.deliveryTime || ""}
                  </span>
                )}
                {!transfer.deliveryDate && transfer.transferDate && (
                  <span className="meta-item">
                    <FiCalendar size={14} />
                    {formatDate(transfer.transferDate)}
                  </span>
                )}
                {transfer.driverName && (
                  <span className="meta-item">
                    <FiUser size={14} />
                    {transfer.driverName}
                  </span>
                )}
                {transfer.vehiclePlate && (
                  <span className="meta-item">
                    <FiTruck size={14} />
                    {transfer.vehiclePlate}
                  </span>
                )}
              </div>
            </div>
            <div className="delivery-info-card__stats">
              <div className="stat-badge">
                <FiPackage size={16} />
                <div>
                  <div className="stat-value">{totalDevices}</div>
                  <div className="stat-label">Ürün</div>
                </div>
              </div>
              <div className="stat-badge">
                <FiShoppingCart size={16} />
                <div>
                  <div className="stat-value">
                    {Object.keys(modelGroups).length}
                  </div>
                  <div className="stat-label">Model</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Transfer Details Grid */}
        <div className="delivery-details-grid">
          {/* Company Sold Card */}
          <div className="delivery-detail-card">
            <div className="delivery-detail-card__header">
              <FiHome size={18} />
              <h4>Sipariş Yapılan Firma</h4>
            </div>
            <div className="delivery-detail-card__body">
              {transfer.saleId?.companyId ? (
                <>
                  <strong>
                    {typeof transfer.saleId.companyId === "object"
                      ? transfer.saleId.companyId.name
                      : "-"}
                  </strong>
                  {/* <p className="text-muted">Firma</p> */}
                  {typeof transfer.saleId.companyId === "object" &&
                    transfer.saleId.companyId.code && (
                      <p className="text-muted small">
                        Kod: {transfer.saleId.companyId.code}
                      </p>
                    )}
                </>
              ) : (
                <p className="text-muted">-</p>
              )}
            </div>
          </div>

          {/* Delivery Info Card */}
          {(transfer.driverName || transfer.vehiclePlate) && (
            <div className="delivery-detail-card">
              <div className="delivery-detail-card__header">
                <FiTruck size={18} />
                <h4>Sevkiyat Bilgileri</h4>
              </div>
              <div className="delivery-detail-card__body">
                {transfer.driverName && (
                  <>
                    <strong>{transfer.driverName}</strong>
                    <p className="text-muted">Sürücü</p>
                  </>
                )}
                {transfer.vehiclePlate && (
                  <>
                    <strong>{transfer.vehiclePlate}</strong>
                    <p className="text-muted">Araç Plakası</p>
                  </>
                )}
                {!transfer.driverName && !transfer.vehiclePlate && (
                  <p className="text-muted">-</p>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Notes Card */}
        {transfer.notes && (
          <div className="delivery-notes-card">
            <div className="delivery-notes-card__header">
              <FiFileText size={18} />
              <h4>Notlar</h4>
            </div>
            <div className="delivery-notes-card__body">
              <p>{transfer.notes}</p>
            </div>
          </div>
        )}

        {/* Devices by Model */}
        <div className="delivery-section">
          <div className="delivery-section__header">
            <FiPackage size={20} />
            <h4>Teslim Edilen Ürünler</h4>
            <Lozenge appearance="success-subtle">{totalDevices} ürün</Lozenge>
          </div>
          {Object.keys(modelGroups).length > 0 ? (
            <div className="delivery-models-list">
              {Object.values(modelGroups).map((modelGroup, index) => (
                <div key={index} className="delivery-model-card">
                  <div
                    className="delivery-model-card__header"
                    role="button"
                    tabIndex={0}
                    onClick={() => toggleModelExpanded(modelGroup)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        toggleModelExpanded(modelGroup);
                      }
                    }}
                    style={{ cursor: "pointer" }}
                  >
                    <div className="delivery-model-card__info">
                      <h5>{modelGroup.modelName}</h5>
                      {modelGroup.modelCode && (
                        <p className="text-muted small">
                          Kod: {modelGroup.modelCode}
                        </p>
                      )}
                    </div>
                    <div className="d-flex align-items-center gap-2">
                      <Lozenge appearance="success-subtle">
                        {modelGroup.devices.length} adet
                      </Lozenge>
                      {(() => {
                        const key = String(getModelKey(modelGroup));
                        const isOpen = !!expandedModels?.[key];
                        return isOpen ? (
                          <FiChevronUp size={18} />
                        ) : (
                          <FiChevronDown size={18} />
                        );
                      })()}
                    </div>
                  </div>
                  {(() => {
                    const key = String(getModelKey(modelGroup));
                    const isOpen = !!expandedModels?.[key];
                    if (!isOpen) return null;

                    const devicesArr = modelGroup.devices || [];
                    const page = Number(modelDevicePages?.[key] || 1);
                    const totalPages = Math.max(
                      1,
                      Math.ceil(devicesArr.length / modelDevicesPerPage),
                    );
                    const safePage = Math.min(Math.max(page, 1), totalPages);
                    const startIdx = (safePage - 1) * modelDevicesPerPage;
                    const endIdx = startIdx + modelDevicesPerPage;
                    const pageItems = devicesArr.slice(startIdx, endIdx);

                    return (
                      <div className="delivery-model-card__devices">
                        {pageItems.map((device, deviceIndex) => (
                          <div
                            key={`${startIdx + deviceIndex}`}
                            className="delivery-device-item"
                          >
                            <FiPackage size={14} />
                            <span>
                              Seri No:{" "}
                              <strong>{device?.serialNumber || "-"}</strong>
                            </span>
                          </div>
                        ))}

                        {devicesArr.length > modelDevicesPerPage && (
                          <div
                            className="d-flex align-items-center justify-content-between mt-2"
                            style={{
                              paddingTop: 10,
                              borderTop: "1px solid rgba(0,0,0,0.06)",
                            }}
                            onClick={(e) => e.stopPropagation()}
                            onKeyDown={(e) => e.stopPropagation()}
                          >
                            <Button
                              appearance="subtle"
                              onClick={() =>
                                setModelPage(modelGroup, safePage - 1)
                              }
                              disabled={safePage <= 1}
                            >
                              Önceki
                            </Button>
                            <span className="text-muted small">
                              Sayfa {safePage} / {totalPages}
                            </span>
                            <Button
                              appearance="subtle"
                              onClick={() =>
                                setModelPage(modelGroup, safePage + 1)
                              }
                              disabled={safePage >= totalPages}
                            >
                              Sonraki
                            </Button>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              ))}
            </div>
          ) : (
            <div className="empty-state">
              <FiPackage size={48} />
              <p>Ürün bulunamadı</p>
            </div>
          )}
        </div>

        {/* Timeline */}
        {/* // {transfer.createdAt && (
        //   <div className="delivery-section">
        //     <div className="delivery-section__header">
        //       <FiCalendar size={20} />
        //       <h4>Geçmiş</h4>
        //     </div>
        //     <div className="delivery-timeline">
        //       <div className="delivery-timeline-item">
        //         <div className="delivery-timeline-marker"></div>
        //         <div className="delivery-timeline-content">
        //           <strong>Oluşturuldu</strong>
        //           <p>{formatDateTime(transfer.createdAt)}</p>
        //         </div>
        //       </div>
        //       {transfer.updatedAt &&
        //         transfer.updatedAt !== transfer.createdAt && (
        //           <div className="delivery-timeline-item">
        //             <div className="delivery-timeline-marker"></div>
        //             <div className="delivery-timeline-content">
        //               <strong>Güncellendi</strong>
        //               <p>{formatDateTime(transfer.updatedAt)}</p>
        //             </div>
        //           </div>
        //         )}
        //     </div>
        //   </div>
        // )} */}
      </CardContainer>

      <ToastContainer>
        {toasts.map((toast) => (
          <Toast
            key={toast.id}
            title={toast.message}
            appearance={toast.type}
            onClose={() => setToasts(toasts.filter((t) => t.id !== toast.id))}
          />
        ))}
      </ToastContainer>

      <PdfPreviewModal
        isOpen={pdfPreview.open}
        onClose={closePdfPreview}
        title={pdfPreview.title}
        previewUrl={pdfPreview.url}
        fileName={pdfPreview.fileName}
        loading={pdfPreview.loading}
        error={pdfPreview.error}
        onDownload={downloadPdfFromPreview}
      />
    </AppPage>
  );
};

export default withAuth(ViewDelivery, ["admin", "employee", "user"]);
